"use client"

import { useForm, type SubmitHandler } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import type { components } from "umbraco"
import { postEggedPlusAccidentReportSubmit } from "api"
import {
  FormShell,
  FormTextField,
  FormFileUpload,
  FormDatePicker,
  FormTimePicker,
  FormCheckboxField,
  FieldRow,
  FieldCol,
  useFormSubmit,
  checkBooleanResponse,
  validateRecipients,
  RecipientsConfigError,
  getShellLabels,
  type FormLanguage,
} from "../../common/forms"
import {
  createSchema,
  type FormValues,
  defaultValues,
  MAX_DAMAGE_PHOTOS,
} from "./formSchema"

type AccidentReportFormData =
  components["schemas"]["AccidentReportFormPropertiesModel"]

function getFieldLabels(locale: string) {
  if (locale === "en") {
    return {
      fullName: "Full Name",
      phone: "Phone",
      email: "Email",
      date: "Incident Date",
      time: "Incident Time",
      location: "Incident Location",
      atFaultName: "At-fault Driver Name",
      atFaultPlate: "At-fault Vehicle Plate",
      thirdPartyPlate: "Third-party Vehicle Plate",
      description: "Notes & incident description",
      damagePhotos: "Color photos of the damage",
      licensePhoto: "Photo of the damaged vehicle license",
    }
  }
  return {
    fullName: "שם מלא",
    phone: "טלפון",
    email: 'דוא"ל',
    date: "תאריך האירוע",
    time: "שעת האירוע",
    location: "מקום האירוע",
    atFaultName: "שם הנהג/ת הפוגע/ת",
    atFaultPlate: "מספר רישוי של הרכב הפוגע",
    thirdPartyPlate: "מספר רישוי רכב צד ג'",
    description: "הערות ותיאור האירוע",
    damagePhotos: "תמונות צבעוניות של הנזק",
    licensePhoto: "צילום רישיון של הרכב הניזוק",
  }
}

interface Props {
  data: AccidentReportFormData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function AccidentReportForm({ data, locale }: Props) {
  const shellLabels = getShellLabels(locale)
  const fieldLabels = getFieldLabels(locale)
  const recipients = data.recipients ?? []
  const showCheckbox = data.showCheckbox ?? false
  const checkboxRequired = showCheckbox && (data.checkboxRequired ?? false)

  const schema = createSchema(locale as FormLanguage, checkboxRequired)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    // EGDE-257: onTouched validates a field on first blur (empty date/time still
    // error) then re-validates on every change, so the moment a valid date/time
    // is entered the red state clears instead of waiting for another blur.
    mode: "onTouched",
  })

  const cultureLocale =
    locale === "he" ? "he-IL" : locale === "en" ? "en-US" : locale

  const { state, isPending, submit, retry } = useFormSubmit<FormValues>({
    mutationFn: async (formData) => {
      const fd = new FormData()
      fd.append("FullName", formData.fullName)
      fd.append("Phone", formData.phone)
      fd.append("Email", formData.email || "")
      fd.append("IncidentDate", formData.incidentDate.toISOString())
      fd.append("IncidentTime", formData.incidentTime)
      fd.append("IncidentLocation", formData.incidentLocation)
      fd.append("AtFaultDriverName", formData.atFaultDriverName || "")
      fd.append("AtFaultLicensePlate", formData.atFaultLicensePlate)
      fd.append("ThirdPartyLicensePlate", formData.thirdPartyLicensePlate)
      fd.append("IncidentDescription", formData.incidentDescription)
      // EGDE-258: the backend model binds ConfirmationCheckbox and renders it in
      // the notification email ("אישור קבלת דיווח"). Without this append the
      // value arrives empty even when the user ticked the consent box.
      // EGDE-445: only send it when the checkbox is actually shown — a hidden
      // checkbox must not arrive as "לא" in the email.
      if (showCheckbox) {
        fd.append("ConfirmationCheckbox", String(!!formData.checkbox))
      }
      fd.append("ReferrerUrl", window.location.href)
      recipients.forEach((r) => fd.append("Recipients", r))
      ;(formData.damagePhotos ?? []).forEach((file) =>
        fd.append("DamagePhotos", file as File, (file as File).name),
      )
      fd.append(
        "VehicleLicensePhoto",
        formData.vehicleLicensePhoto,
        formData.vehicleLicensePhoto.name,
      )
      return checkBooleanResponse(
        await postEggedPlusAccidentReportSubmit(fd, undefined, cultureLocale),
      )
    },
    onReset: reset,
  })

  if (!validateRecipients(recipients)) {
    return <RecipientsConfigError formName="AccidentReportForm" />
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => submit(formData)

  return (
    <FormShell
      title={data.title}
      subtitle={data.subtitle}
      labels={shellLabels}
      state={state}
      isPending={isPending}
      onSubmit={handleSubmit(onSubmit)}
      onRetry={retry}
      footer={
        showCheckbox ? (
          <FormCheckboxField
            control={control}
            name="checkbox"
            markup={data.checkboxText?.markup}
            required={checkboxRequired}
            error={errors.checkbox?.message}
          />
        ) : null
      }
    >
      {/* Layout mirrors the Figma design: a strict 6-row × 2-column grid.
          In RTL the first column of each row renders on the right. */}
      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="fullName"
            label={fieldLabels.fullName}
            required
            autoComplete="name"
            error={errors.fullName?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="phone"
            label={fieldLabels.phone}
            required
            type="tel"
            autoComplete="tel"
            error={errors.phone?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="email"
            label={fieldLabels.email}
            type="email"
            autoComplete="email"
            error={errors.email?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormDatePicker
            control={control}
            name="incidentDate"
            label={fieldLabels.date}
            locale={locale}
            required
            error={errors.incidentDate?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          <FormTimePicker
            control={control}
            name="incidentTime"
            label={fieldLabels.time}
            locale={locale}
            required
            error={errors.incidentTime?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="incidentLocation"
            label={fieldLabels.location}
            required
            error={errors.incidentLocation?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="atFaultDriverName"
            label={fieldLabels.atFaultName}
            error={errors.atFaultDriverName?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="atFaultLicensePlate"
            label={fieldLabels.atFaultPlate}
            required
            inputMode="numeric"
            error={errors.atFaultLicensePlate?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="thirdPartyLicensePlate"
            label={fieldLabels.thirdPartyPlate}
            required
            inputMode="numeric"
            error={errors.thirdPartyLicensePlate?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="incidentDescription"
            label={fieldLabels.description}
            required
            multiline
            rows={1}
            error={errors.incidentDescription?.message}
          />
        </FieldCol>
      </FieldRow>

      <FieldRow>
        <FieldCol>
          {/* EGDE-340: damage photos are optional — no required asterisk. */}
          <FormFileUpload
            control={control}
            name="damagePhotos"
            label={fieldLabels.damagePhotos}
            accept="image/jpeg,image/jpg,image/png"
            multiple
            maxFiles={MAX_DAMAGE_PHOTOS}
            iconVariant="image"
            error={errors.damagePhotos?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormFileUpload
            control={control}
            name="vehicleLicensePhoto"
            label={fieldLabels.licensePhoto}
            accept="image/jpeg,image/jpg,image/png"
            required
            error={errors.vehicleLicensePhoto?.message}
          />
        </FieldCol>
      </FieldRow>
    </FormShell>
  )
}
